/**
 * Accessibility utilities for CareCal frontend
 * Provides helper functions for managing focus, ARIA attributes, and screen reader announcements
 */

import * as React from 'react'

/**
 * Manages focus trapping within a container (e.g., modals)
 */
export class FocusTrap {
  private container: HTMLElement
  private focusableElements: HTMLElement[]
  private firstFocusableElement: HTMLElement | null = null
  private lastFocusableElement: HTMLElement | null = null
  private previousActiveElement: Element | null = null

  constructor(container: HTMLElement) {
    this.container = container
    this.focusableElements = []
    this.updateFocusableElements()
  }

  /**
   * Updates the list of focusable elements within the container
   */
  private updateFocusableElements() {
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ].join(', ')

    this.focusableElements = Array.from(
      this.container.querySelectorAll(focusableSelectors)
    ) as HTMLElement[]

    this.firstFocusableElement = this.focusableElements[0] || null
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1] || null
  }

  /**
   * Activates the focus trap
   */
  activate() {
    this.previousActiveElement = document.activeElement
    this.updateFocusableElements()

    // Focus the first focusable element
    if (this.firstFocusableElement) {
      this.firstFocusableElement.focus()
    }

    // Add event listener for tab key
    this.container.addEventListener('keydown', this.handleKeyDown)
  }

  /**
   * Deactivates the focus trap and restores previous focus
   */
  deactivate() {
    this.container.removeEventListener('keydown', this.handleKeyDown)

    // Restore focus to the previously active element
    if (this.previousActiveElement && 'focus' in this.previousActiveElement) {
      ;(this.previousActiveElement as HTMLElement).focus()
    }
  }

  /**
   * Handles keydown events for focus trapping
   */
  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return

    this.updateFocusableElements()

    if (this.focusableElements.length === 0) {
      event.preventDefault()
      return
    }

    if (event.shiftKey) {
      // Shift + Tab (backward)
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault()
        this.lastFocusableElement?.focus()
      }
    } else {
      // Tab (forward)
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault()
        this.firstFocusableElement?.focus()
      }
    }
  }
}

/**
 * Announces text to screen readers using ARIA live regions
 */
export class ScreenReaderAnnouncer {
  private static instance: ScreenReaderAnnouncer | null = null
  private liveRegion: HTMLElement | null = null

  private constructor() {
    this.createLiveRegion()
  }

  /**
   * Gets the singleton instance of the announcer
   */
  static getInstance(): ScreenReaderAnnouncer {
    if (!ScreenReaderAnnouncer.instance) {
      ScreenReaderAnnouncer.instance = new ScreenReaderAnnouncer()
    }
    return ScreenReaderAnnouncer.instance
  }

  /**
   * Creates the ARIA live region for announcements
   */
  private createLiveRegion() {
    if (this.liveRegion) return

    this.liveRegion = document.createElement('div')
    this.liveRegion.setAttribute('aria-live', 'polite')
    this.liveRegion.setAttribute('aria-atomic', 'true')
    this.liveRegion.setAttribute('class', 'sr-only')
    this.liveRegion.style.position = 'absolute'
    this.liveRegion.style.left = '-10000px'
    this.liveRegion.style.width = '1px'
    this.liveRegion.style.height = '1px'
    this.liveRegion.style.overflow = 'hidden'

    document.body.appendChild(this.liveRegion)
  }

  /**
   * Announces a message to screen readers
   * @param message - The message to announce
   * @param priority - The priority level ('polite' or 'assertive')
   */
  announce(message: string, priority: 'polite' | 'assertive' = 'polite') {
    if (!this.liveRegion) {
      this.createLiveRegion()
    }

    if (this.liveRegion) {
      this.liveRegion.setAttribute('aria-live', priority)
      this.liveRegion.textContent = message

      // Clear the message after a short delay to allow for re-announcements
      setTimeout(() => {
        if (this.liveRegion) {
          this.liveRegion.textContent = ''
        }
      }, 1000)
    }
  }
}

/**
 * Generates unique IDs for ARIA relationships
 */
export function generateId(prefix: string = 'carecal'): string {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Checks if an element is visible to screen readers
 */
export function isVisibleToScreenReader(element: HTMLElement): boolean {
  const style = window.getComputedStyle(element)
  return !(
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0' ||
    element.hasAttribute('aria-hidden') ||
    element.hidden
  )
}

/**
 * Gets the accessible name of an element
 */
export function getAccessibleName(element: HTMLElement): string {
  // Check aria-label first
  const ariaLabel = element.getAttribute('aria-label')
  if (ariaLabel) return ariaLabel

  // Check aria-labelledby
  const ariaLabelledBy = element.getAttribute('aria-labelledby')
  if (ariaLabelledBy) {
    const labelElement = document.getElementById(ariaLabelledBy)
    if (labelElement) return labelElement.textContent || ''
  }

  // Check associated label for form elements
  if (
    element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    element.tagName === 'SELECT'
  ) {
    const id = element.getAttribute('id')
    if (id) {
      const label = document.querySelector(`label[for="${id}"]`)
      if (label) return label.textContent || ''
    }
  }

  // Fall back to text content
  return element.textContent || ''
}

/**
 * Keyboard navigation utilities
 */
export const KeyboardNavigation = {
  /**
   * Handles arrow key navigation for lists and grids
   */
  handleArrowKeys(
    event: KeyboardEvent,
    items: HTMLElement[],
    currentIndex: number,
    options: {
      orientation?: 'horizontal' | 'vertical' | 'both'
      wrap?: boolean
      columns?: number
    } = {}
  ): number {
    const { orientation = 'vertical', wrap = true, columns = 1 } = options
    let newIndex = currentIndex

    switch (event.key) {
      case 'ArrowUp':
        if (orientation === 'vertical' || orientation === 'both') {
          event.preventDefault()
          newIndex = currentIndex - columns
          if (newIndex < 0) {
            newIndex = wrap ? items.length - 1 : 0
          }
        }
        break

      case 'ArrowDown':
        if (orientation === 'vertical' || orientation === 'both') {
          event.preventDefault()
          newIndex = currentIndex + columns
          if (newIndex >= items.length) {
            newIndex = wrap ? 0 : items.length - 1
          }
        }
        break

      case 'ArrowLeft':
        if (orientation === 'horizontal' || orientation === 'both') {
          event.preventDefault()
          newIndex = currentIndex - 1
          if (newIndex < 0) {
            newIndex = wrap ? items.length - 1 : 0
          }
        }
        break

      case 'ArrowRight':
        if (orientation === 'horizontal' || orientation === 'both') {
          event.preventDefault()
          newIndex = currentIndex + 1
          if (newIndex >= items.length) {
            newIndex = wrap ? 0 : items.length - 1
          }
        }
        break

      case 'Home':
        event.preventDefault()
        newIndex = 0
        break

      case 'End':
        event.preventDefault()
        newIndex = items.length - 1
        break
    }

    if (newIndex !== currentIndex && items[newIndex]) {
      items[newIndex].focus()
    }

    return newIndex
  },

  /**
   * Sets up roving tabindex for a group of elements
   */
  setupRovingTabindex(items: HTMLElement[], initialIndex: number = 0) {
    items.forEach((item, index) => {
      item.setAttribute('tabindex', index === initialIndex ? '0' : '-1')
    })
  },
}

/**
 * Hook for managing focus trap in React components
 */
export function useFocusTrap(isActive: boolean, containerRef: React.RefObject<HTMLElement | null>) {
  React.useEffect(() => {
    if (!isActive || !containerRef.current) return

    const focusTrap = new FocusTrap(containerRef.current)
    focusTrap.activate()

    return () => {
      focusTrap.deactivate()
    }
  }, [isActive, containerRef])
}

/**
 * Hook for screen reader announcements
 */
export function useScreenReaderAnnouncement() {
  const announcer = ScreenReaderAnnouncer.getInstance()

  return React.useCallback(
    (message: string, priority: 'polite' | 'assertive' = 'polite') => {
      announcer.announce(message, priority)
    },
    [announcer]
  )
}
